import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Plus, Edit, Trash2, X, Save, Truck, Loader2, Search, Filter, Layers, ChevronDown, ChevronRight, ArrowUp, ArrowDown, Upload, ArrowRightLeft, Eye, RotateCcw } from 'lucide-react';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { uiMetadataService } from '../services/uiMetadataService';
import { driverService } from '../services/driverService';
import { customerService } from '../services/customerService';
import { corporateCarrierService } from '../services/corporateCarrierService';
import { settingsService } from '../services/settingsService';
import { citiesService, type City, type District } from '../services/citiesService';
import { useAppAlert } from '../components/AppAlertProvider';
import { WorkiomTablePagination } from '../components/WorkiomTablePagination';
import { exportRowsToExcel } from '../utils/excelExport';
import { useAuth } from '../contexts/AuthContext';
import { hasUserRole } from '../utils/userRoles';
import * as XLSX from 'xlsx-js-style';

interface Vehicle {
  id: string;
  plate: string;
  trailerPlate?: string;
  assignedDriverId?: string;
  plateNumber?: string;
  vehicleType: 'Tır' | 'Kamyon' | 'Kamyonet' | 'Çekici';
  brand: string;
  model: string;
  year: number;
  capacity: string;
  volume: string;
  fuelType: 'Dizel' | 'Benzin' | 'LPG' | 'Elektrik';
  ownershipType: 'Filo' | 'Kiralık' | 'Spot';
  status: 'Aktif' | 'Bakımda' | 'Arızalı' | 'Pasif';
  lastMaintenanceDate: string;
  nextMaintenanceDate: string;
  insuranceExpiry: string;
  inspectionExpiry: string;
  notes: string;
  assignedDriverName?: string;
  activeDrivers?: Array<{ id: string; name: string }>;
  latitude?: number | null;
  longitude?: number | null;
  remainingDailyDrivePeriod?: string;
  currentWeekHours?: string;
  twoWeekHours?: string;
  regionDeliveryRestrictions?: Array<{ city: string; district: string }>;
  preferredDeliveryPoints?: Array<{ id: string; label: string }>;
  maxDailyIntraCityDeliveries?: number | null;
  excludeFromBulkAssignment?: boolean;
  externalTransportAccountUserId?: string;
  externalTransportAccountName?: string;
}

const DEPRECATED_VEHICLE_KEYS = new Set(['staticCity', 'assignmentCityLimit', 'intraCityDeliveryLimit']);
const EXTERNAL_VEHICLE_HIDDEN_KEYS = new Set([
  'remainingDailyDrivePeriod',
  'currentWeekHours',
  'twoWeekHours',
  'latitude',
  'longitude',
  'regionDeliveryRestrictions',
  'maxDailyIntraCityDeliveries',
  'preferredDeliveryPoints',
  'excludeFromBulkAssignment',
  'lastMaintenanceDate',
  'nextMaintenanceDate',
  'insuranceExpiry',
  'inspectionExpiry',
]);

type VehicleTableView = {
  visibleColumns?: string[];
  columnFilters?: Record<string, string>;
  groupBy?: string;
  sortConfig?: { field: string; direction: 'asc' | 'desc' } | null;
};

const getStoredUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return String(user?.id || 'anonymous');
  } catch {
    return 'anonymous';
  }
};

const mapApiVehicleToUi = (vehicle: any): Vehicle => ({
  id: String(vehicle.id),
  plate: vehicle.cekici_plaka || vehicle.plate || '',
  plateNumber: vehicle.cekici_plaka || vehicle.plate || '',
  vehicleType:
    vehicle.vehicle_type === 'tır' ? 'Tır' :
    vehicle.vehicle_type === 'kamyon' ? 'Kamyon' :
    vehicle.vehicle_type === 'kamyonet' ? 'Kamyonet' : 'Çekici',
  brand: vehicle.brand || '',
  model: vehicle.model || '',
  year: Number(vehicle.year || 0),
  capacity: vehicle.capacity_kg ? String(Number(vehicle.capacity_kg) / 1000) : '',
  volume: vehicle.capacity_m3 ? String(vehicle.capacity_m3) : '',
  fuelType:
    vehicle.fuel_type === 'dizel' ? 'Dizel' :
    vehicle.fuel_type === 'benzin' ? 'Benzin' :
    vehicle.fuel_type === 'lpg' ? 'LPG' : 'Elektrik',
  ownershipType:
    vehicle.owner_type === 'own' ? 'Filo' :
    vehicle.owner_type === 'rental' ? 'Kiralık' : 'Spot',
  status:
    vehicle.status === 'active' ? 'Aktif' :
    vehicle.status === 'maintenance' ? 'Bakımda' : 'Pasif',
  lastMaintenanceDate: normalizeDateOnly(vehicle.last_maintenance_date_text || vehicle.last_maintenance_date) || '',
  nextMaintenanceDate: normalizeDateOnly(vehicle.next_maintenance_date_text || vehicle.next_maintenance_date) || '',
  insuranceExpiry: normalizeDateOnly(vehicle.insurance_expiry_text || vehicle.insurance_expiry) || '',
  inspectionExpiry: normalizeDateOnly(vehicle.license_expiry_text || vehicle.license_expiry) || '',
  notes: vehicle.notes || '',
  trailerPlate: vehicle.linked_trailer_plate || vehicle.dorse_plaka || '',
  assignedDriverName: vehicle.driver_name || '',
  activeDrivers: (() => {
    try {
      const raw = vehicle.active_drivers_json;
      if (!raw) return [];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed.map((d: any) => ({ id: String(d.id), name: d.name || '' })).filter((d) => d.name) : [];
    } catch { return []; }
  })(),
  assignedDriverId: vehicle.assigned_driver_id ? String(vehicle.assigned_driver_id) : '',
  latitude: vehicle.latitude != null ? Number(vehicle.latitude) : null,
  longitude: vehicle.longitude != null ? Number(vehicle.longitude) : null,
  remainingDailyDrivePeriod: vehicle.available_drive_period || '',
  currentWeekHours: vehicle.current_week_hours || '',
  twoWeekHours: vehicle.two_week_hours || '',
  regionDeliveryRestrictions: (() => {
    try {
      const raw = vehicle.region_restrictions_json;
      if (!raw) return [];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed)
        ? parsed
          .map((item: any) => ({ city: String(item?.city || '').trim(), district: String(item?.district || '').trim() }))
          .filter((item: any) => item.city && item.district)
        : [];
    } catch {
      return [];
    }
  })(),
  maxDailyIntraCityDeliveries: vehicle.max_daily_intra_city_deliveries != null ? Number(vehicle.max_daily_intra_city_deliveries) : null,
  excludeFromBulkAssignment: vehicle.exclude_from_bulk_assignment == 1 || vehicle.exclude_from_bulk_assignment === true,
  externalTransportAccountUserId: vehicle.external_transport_account_user_id != null ? String(vehicle.external_transport_account_user_id) : '',
  externalTransportAccountName: vehicle.external_transport_account_name || vehicle.external_transport_company_title || '',
  preferredDeliveryPoints: (() => {
    try {
      const raw = vehicle.preferred_delivery_points_json;
      if (!raw) return [];
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const result = Array.isArray(parsed) ? parsed.map((item: any) => ({ id: String(item.id), label: item.label || item.name || String(item.id) })) : [];
      console.log('[VMDebug] mapApiVehicleToUi preferredDeliveryPoints:', { raw: raw?.slice(0,200), parsed, result, resultIds: result.map(r => r.id) });
      return result;
    } catch (e) {
      console.error('[VMDebug] preferredDeliveryPoints parse error:', e);
      return [];
    }
  })(),
});

const normalizeDateOnly = (value?: string | null) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const dateMatch = String(value).match(/\d{4}-\d{2}-\d{2}/);
  if (dateMatch) return dateMatch[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const mapUiVehicleToApi = (vehicle: Partial<Vehicle>, externalTransportAccountUserId?: string) => ({
  cekici_plaka: vehicle.plate,
  vehicle_type:
    vehicle.vehicleType === 'Tır' ? 'tır' :
    vehicle.vehicleType === 'Kamyon' ? 'kamyon' :
    vehicle.vehicleType === 'Kamyonet' ? 'kamyonet' : 'çekici',
  brand: vehicle.brand,
  model: vehicle.model,
  year: vehicle.year,
  capacity_kg: vehicle.capacity ? Math.round(Number(vehicle.capacity) * 1000) : null,
  capacity_m3: vehicle.volume ? Number(vehicle.volume) : null,
  fuel_type:
    vehicle.fuelType === 'Dizel' ? 'dizel' :
    vehicle.fuelType === 'Benzin' ? 'benzin' :
    vehicle.fuelType === 'LPG' ? 'lpg' : 'elektrik',
  owner_type:
    vehicle.ownershipType === 'Filo' ? 'own' :
    vehicle.ownershipType === 'Kiralık' ? 'rental' : 'external',
  status:
    vehicle.status === 'Aktif' ? 'active' :
    vehicle.status === 'Bakımda' ? 'maintenance' : 'inactive',
  last_maintenance_date: normalizeDateOnly(vehicle.lastMaintenanceDate),
  next_maintenance_date: normalizeDateOnly(vehicle.nextMaintenanceDate),
  insurance_expiry: normalizeDateOnly(vehicle.insuranceExpiry),
  license_expiry: normalizeDateOnly(vehicle.inspectionExpiry),
  notes: vehicle.notes || null,
  dorse_plaka: vehicle.trailerPlate || null,
  assigned_driver_id: (vehicle as any).assignedDriverId || null,
  region_restrictions_json: JSON.stringify(Array.isArray(vehicle.regionDeliveryRestrictions) ? vehicle.regionDeliveryRestrictions : []),
  max_daily_intra_city_deliveries: vehicle.maxDailyIntraCityDeliveries != null ? Number(vehicle.maxDailyIntraCityDeliveries) : null,
  exclude_from_bulk_assignment: vehicle.excludeFromBulkAssignment ? 1 : 0,
  preferred_delivery_points_json: JSON.stringify(Array.isArray(vehicle.preferredDeliveryPoints) ? vehicle.preferredDeliveryPoints : []),
  external_transport_account_user_id: externalTransportAccountUserId || undefined,
});

export function VehicleManagement() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const corporateCompanyId = hasUserRole(user, 'corporate-external-transport', 'corporate-external-transport-user') ? String((user as any)?.externalTransportAccountUserId || user?.id || '').trim() : '';
  const companyIdFilter = corporateCompanyId || String(searchParams.get('companyId') || '').trim();
  const ownerTypeFilter = String(searchParams.get('ownerType') || '').trim();
  const isExternalOwnerView = ownerTypeFilter === 'external' || Boolean(corporateCompanyId);
  const tableViewSettingKey = `vehicle_management_table_view_${isExternalOwnerView ? 'external' : 'all'}_${companyIdFilter || 'all'}_${getStoredUserId()}`;
  const { showAlert, showConfirm } = useAppAlert();
  const bulkUpdateInputRef = useRef<HTMLInputElement | null>(null);
  const [data, setData] = useState<Vehicle[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Vehicle | null>(null);
  const [formData, setFormData] = useState<Partial<Vehicle>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metadataFields, setMetadataFields] = useState<Array<{ key: string; label: string }>>([]);
  const [metadataColumns, setMetadataColumns] = useState<Array<{ key: string; label: string; visibleByDefault?: boolean }>>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [internalDrivers, setInternalDrivers] = useState<any[]>([]);
  const [trailers, setTrailers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string; city?: string; district?: string; code?: string }>>([]);
  const [carrierCompanies, setCarrierCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [carrierCompanySearch, setCarrierCompanySearch] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [regionDistricts, setRegionDistricts] = useState<District[]>([]);
  const [selectedRegionCityId, setSelectedRegionCityId] = useState('');
  const [selectedRegionDistrictIds, setSelectedRegionDistrictIds] = useState<string[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showGrouping, setShowGrouping] = useState(false);
  const [groupBy, setGroupBy] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);
  const [tableViewLoaded, setTableViewLoaded] = useState(false);

  useEffect(() => {
    loadVehicles();
    loadMetadata();
    loadInternalDrivers();
    loadTrailers();
    loadCustomers();
    loadCarrierCompanies();
    loadCities();
    loadTableMetadata();
  }, [companyIdFilter, isExternalOwnerView]);

  useEffect(() => {
    if (!selectedRegionCityId) {
      setRegionDistricts([]);
      setSelectedRegionDistrictIds([]);
      return;
    }
    citiesService.getDistricts(Number(selectedRegionCityId))
      .then((response: any) => {
        setRegionDistricts(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => setRegionDistricts([]));
  }, [selectedRegionCityId]);

  const loadCustomers = async () => {
    try {
      const response: any = await customerService.getCustomers({ status: 'active' });
      const items = Array.isArray(response?.data) ? response.data : [];
      setCustomers(items.map((item: any) => ({ id: String(item.id), name: item.company_name || item.name || '-', city: item.city || '', district: item.district || '', code: item.code || '' })));
    } catch (error) {
      console.error('Failed to load customers for vehicle form:', error);
      setCustomers([]);
    }
  };

  const loadCarrierCompanies = async () => {
    try {
      const response: any = await corporateCarrierService.getAll({ status: 'active' });
      const items = Array.isArray(response?.data) ? response.data : [];
      setCarrierCompanies(items.map((item: any) => ({ id: String(item.id), name: item.fullName || item.companyTitle || item.username || `Firma #${item.id}` })));
    } catch {
      setCarrierCompanies([]);
    }
  };

  const loadCities = async () => {
    try {
      const response: any = await citiesService.getAll();
      setCities(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load cities for vehicle form:', error);
      setCities([]);
    }
  };

  const loadTrailers = async () => {
    try {
      const response: any = await trailerService.getTrailers();
      const items = Array.isArray(response?.data) ? response.data : [];
      setTrailers(items.map((item: any) => ({ id: String(item.id), plate: item.plate || '', linkedVehicleId: item.linked_vehicle_id ? String(item.linked_vehicle_id) : '' })));
    } catch (error) {
      console.error('Failed to load trailers for vehicle form:', error);
      setTrailers([]);
    }
  };

  const loadInternalDrivers = async () => {
    try {
      const response: any = await driverService.getDrivers({ type: 'internal', status: 'active' });
      const items = Array.isArray(response?.data) ? response.data : [];
      setInternalDrivers(items.map((item: any) => ({ id: String(item.id), name: item.full_name || '-', plate: item.cekici_plaka || '' })));
    } catch (error) {
      console.error('Failed to load drivers for vehicle form:', error);
    }
  };

  const loadMetadata = async () => {
    try {
      const response: any = await uiMetadataService.getModuleForm('vehicle');
      const tabs = Array.isArray(response?.data?.tabs) ? response.data.tabs : [];
      const mainTab = tabs.find((tab: any) => tab.key === 'main') || tabs[0];
      const fields = (Array.isArray(mainTab?.fields) ? mainTab.fields : []).filter((field: any) => !DEPRECATED_VEHICLE_KEYS.has(String(field?.key || '')));
      setMetadataFields(fields);
    } catch (error) {
      console.error('Failed to load vehicle metadata:', error);
    }
  };

  const loadTableMetadata = async () => {
    try {
      const response: any = await uiMetadataService.getModuleTable('vehicle');
      const columns = (Array.isArray(response?.data?.columns) ? response.data.columns : []).filter((column: any) => !DEPRECATED_VEHICLE_KEYS.has(String(column?.key || '')));
      setMetadataColumns(columns);
    } catch (error) {
      console.error('Failed to load vehicle table metadata:', error);
    }
  };

  const loadVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vehicleService.getVehicles({
        ...(companyIdFilter ? { externalTransportAccountUserId: companyIdFilter } : {}),
        ownerType: isExternalOwnerView ? 'external' : 'own',
      });
      if (response.success && response.data) {
        const mapped = response.data.map(mapApiVehicleToUi);
        setData(isExternalOwnerView ? mapped : mapped.filter((vehicle: Vehicle) => !vehicle.externalTransportAccountUserId && vehicle.ownershipType === 'Filo'));
      } else {
        setData([]);
      }
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Araçlar yüklenirken bir hata oluştu');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const saveVehicles = (vehicles: Vehicle[]) => {
    setData(vehicles);
  };

  const filteredPreferredDeliveryCustomers = useMemo(() => {
    const term = customerSearchTerm.trim().toLocaleLowerCase('tr-TR');
    if (!term) return customers;
    return customers.filter((customer) => `${customer.name} ${customer.code || ''} ${customer.city || ''} ${customer.district || ''}`.toLocaleLowerCase('tr-TR').includes(term));
  }, [customerSearchTerm, customers]);

  const formatRegionRestrictionLabel = (item: { city: string; district: string }) => [item.city, item.district].filter(Boolean).join(' / ');

  const addRegionRestriction = () => {
    const city = cities.find((item) => String(item.id) === selectedRegionCityId);
    const selectedDistricts = regionDistricts.filter((item) => selectedRegionDistrictIds.includes(String(item.id)));
    if (!city || selectedDistricts.length === 0) return;
    setFormData((current) => {
      const currentItems = Array.isArray(current.regionDeliveryRestrictions) ? current.regionDeliveryRestrictions : [];
      const additions = selectedDistricts
        .map((district) => ({ city: city.name, district: district.name }))
        .filter((nextItem) => !currentItems.some((item) => item.city === nextItem.city && item.district === nextItem.district));
      if (additions.length === 0) return current;
      return { ...current, regionDeliveryRestrictions: [...currentItems, ...additions] };
    });
    setSelectedRegionDistrictIds([]);
  };

  const removeRegionRestriction = (itemToRemove: { city: string; district: string }) => {
    setFormData((current) => ({
      ...current,
      regionDeliveryRestrictions: (current.regionDeliveryRestrictions || []).filter((item) => !(item.city === itemToRemove.city && item.district === itemToRemove.district)),
    }));
  };

  const columns = [
    { key: 'plate', label: 'Çekici' },
    { key: 'externalTransportAccountName', label: 'Kurumsal Nakliye Firması' },
    { key: 'assignedDriverName', label: 'Atanmış Sürücüler' },
    { key: 'trailerPlate', label: 'Dorse' },
    { key: 'vehicleType', label: 'Araç Tipi' },
    { key: 'brand', label: 'Marka' },
    { key: 'model', label: 'Model' },
    { key: 'year', label: 'Yıl' },
    { key: 'capacity', label: 'Kapasite (ton)' },
    { key: 'volume', label: 'Hacim' },
    { key: 'fuelType', label: 'Yakıt Tipi' },
    { key: 'ownershipType', label: 'Mülkiyet' },
    { key: 'remainingDailyDrivePeriod', label: 'Günlük Kalan Süre' },
    { key: 'currentWeekHours', label: 'Haftalık Kalan Süre' },
    { key: 'twoWeekHours', label: '2 Haftalık Kalan Süre' },
    { key: 'latitude', label: 'Enlem' },
    { key: 'longitude', label: 'Boylam' },
    { key: 'regionDeliveryRestrictions', label: 'Bölge Teslimat Kısıtı' },
    { key: 'maxDailyIntraCityDeliveries', label: 'Günlük Şehir İçi Sipariş Limiti' },
    { key: 'preferredDeliveryPoints', label: 'Öncelikli Teslim Noktaları' },
    { key: 'status', label: 'Durum' },
    { key: 'lastMaintenanceDate', label: 'Son Bakım' },
    { key: 'nextMaintenanceDate', label: 'Sonraki Bakım' },
    { key: 'insuranceExpiry', label: 'Sigorta Bitiş' },
    { key: 'excludeFromBulkAssignment', label: 'Toplu Atamada Kullanma' },
    { key: 'inspectionExpiry', label: 'Muayene Bitiş' },
    { key: 'notes', label: 'Notlar' },
  ];

  const allResolvedColumns = useMemo(() => {
    const base = metadataColumns.length > 0 ? metadataColumns.filter((column) => column.visibleByDefault !== false) : columns;
    const requiredKeys = new Set(base.map((column) => column.key));
    const requiredOperationalColumns = columns
      .filter((column) => ['remainingDailyDrivePeriod', 'currentWeekHours', 'twoWeekHours', 'latitude', 'longitude'].includes(column.key))
      .filter((column) => !requiredKeys.has(column.key));
    const requiredCorporateColumns = isExternalOwnerView
      ? columns.filter((column) => ['externalTransportAccountName', 'trailerPlate'].includes(column.key) && !requiredKeys.has(column.key))
      : [];
    const resolved = [...base, ...requiredCorporateColumns, ...requiredOperationalColumns];
    return isExternalOwnerView ? resolved.filter((column) => !EXTERNAL_VEHICLE_HIDDEN_KEYS.has(column.key)) : resolved;
  }, [metadataColumns, isExternalOwnerView]);

  const resolvedColumns = useMemo(() => {
    const available = new Set(allResolvedColumns.map((column) => column.key));
    const visible = visibleColumnKeys.length > 0 ? visibleColumnKeys.filter((key) => available.has(key)) : allResolvedColumns.map((column) => column.key);
    return visible.map((key) => allResolvedColumns.find((column) => column.key === key)).filter(Boolean) as typeof allResolvedColumns;
  }, [allResolvedColumns, visibleColumnKeys]);

  useEffect(() => {
    if (allResolvedColumns.length === 0 || tableViewLoaded) return;
    settingsService.getMySettingValue<VehicleTableView | null>(tableViewSettingKey, null)
      .then((view) => {
        const available = new Set(allResolvedColumns.map((column) => column.key));
        if (view?.visibleColumns?.length) {
          const nextVisible = view.visibleColumns.filter((key) => available.has(key));
          if (isExternalOwnerView && available.has('externalTransportAccountName') && !nextVisible.includes('externalTransportAccountName')) nextVisible.splice(Math.min(1, nextVisible.length), 0, 'externalTransportAccountName');
          if (isExternalOwnerView && available.has('trailerPlate') && !nextVisible.includes('trailerPlate')) nextVisible.splice(Math.min(2, nextVisible.length), 0, 'trailerPlate');
          setVisibleColumnKeys(nextVisible);
        }
        else setVisibleColumnKeys(allResolvedColumns.map((column) => column.key));
        if (view?.columnFilters) setColumnFilters(view.columnFilters);
        if (view?.groupBy && available.has(view.groupBy)) setGroupBy(view.groupBy);
        if (view?.sortConfig && available.has(view.sortConfig.field)) setSortConfig(view.sortConfig);
      })
      .catch(() => setVisibleColumnKeys(allResolvedColumns.map((column) => column.key)))
      .finally(() => setTableViewLoaded(true));
  }, [allResolvedColumns, tableViewLoaded, tableViewSettingKey]);

  const saveTableView = async () => {
    await settingsService.saveMySetting(tableViewSettingKey, { visibleColumns: visibleColumnKeys, columnFilters, groupBy, sortConfig }, { category: 'ui', description: 'Vehicle management table view' });
    showAlert({ tone: 'success', title: 'Görünüm Kaydedildi', message: 'Kolon, filtre, sıralama ve gruplama ayarları kaydedildi.' });
  };

  const resetTableView = () => {
    setVisibleColumnKeys(allResolvedColumns.map((column) => column.key));
    setColumnFilters({});
    setGroupBy('');
    setSortConfig(null);
  };

  const filteredVehicles = useMemo(() => data.filter((item) => {
    const activeDriverNames = (item.activeDrivers || []).map(d => d.name).join(' ');
    const matchesSearch = `${item.plate} ${item.trailerPlate || ''} ${item.externalTransportAccountName || ''} ${item.brand} ${item.model} ${item.assignedDriverName || ''} ${activeDriverNames}`.toLocaleLowerCase('tr-TR').includes(searchTerm.toLocaleLowerCase('tr-TR'));
    const matchesFilters = Object.entries(columnFilters).every(([key, value]) => !value || String((item as any)[key] || '').toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR')));
    return matchesSearch && matchesFilters;
  }), [data, searchTerm, columnFilters]);

  const sortedVehicles = useMemo(() => {
    if (!sortConfig) return filteredVehicles;
    return [...filteredVehicles].sort((a: any, b: any) => {
      const left = String(a?.[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      const right = String(b?.[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
      if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredVehicles, sortConfig]);

  const groupedVehicles = useMemo(() => {
    if (!groupBy) return { all: sortedVehicles };
    return sortedVehicles.reduce((acc: Record<string, Vehicle[]>, item) => {
      const key = String((item as any)[groupBy] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [sortedVehicles, groupBy]);

  const handleExportExcel = () => exportRowsToExcel(sortedVehicles as any[], `vehicle-management-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'VehicleManagement' });

  useEffect(() => {
    setPage(1);
  }, [searchTerm, columnFilters, groupBy, sortConfig]);

  const handleSort = (field: string) => {
    setSortConfig((current) => {
      if (!current || current.field !== field) return { field, direction: 'asc' };
      if (current.direction === 'asc') return { field, direction: 'desc' };
      return null;
    });
  };

  const renderSortableHeader = (label: string, field: string) => (
    <button type="button" onClick={() => handleSort(field)} className="inline-flex items-center gap-1 font-semibold text-gray-700">
      <span>{label}</span>
      {sortConfig?.field === field ? (sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />) : <ArrowUp className="w-3 h-3 text-gray-300" />}
    </button>
  );

  const openForm = async (item?: Vehicle) => {
    setCustomerSearchTerm('');
    setSelectedRegionCityId('');
    setSelectedRegionDistrictIds([]);
    if (item) {
      setLoadingEdit(true);
      try {
        const response: any = await vehicleService.getVehicle(item.id);
        const resolvedItem = response?.data ? mapApiVehicleToUi(response.data) : item;
        console.log('[VMDebug] openForm resolvedItem preferredDeliveryPoints:', resolvedItem?.preferredDeliveryPoints?.map((p: any) => p.id));
        setEditingItem(resolvedItem);
        setFormData(resolvedItem);
        setCarrierCompanySearch(carrierCompanies.find((company) => company.id === String(resolvedItem.externalTransportAccountUserId || ''))?.name || '');
      } catch (error) {
        console.error('Failed to load full vehicle for edit:', error);
        setEditingItem(item);
        setFormData(item);
        setCarrierCompanySearch(carrierCompanies.find((company) => company.id === String(item.externalTransportAccountUserId || ''))?.name || '');
      } finally {
        setLoadingEdit(false);
        setIsFormOpen(true);
      }
    } else {
      setEditingItem(null);
      setCarrierCompanySearch(carrierCompanies.find((company) => company.id === companyIdFilter)?.name || '');
      setFormData({ status: 'Aktif', ownershipType: isExternalOwnerView || companyIdFilter ? 'Spot' : 'Filo', fuelType: 'Dizel', regionDeliveryRestrictions: [], maxDailyIntraCityDeliveries: null, preferredDeliveryPoints: [], excludeFromBulkAssignment: false, externalTransportAccountUserId: companyIdFilter || '' });
      setIsFormOpen(true);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData({});
    setSelectedRegionCityId('');
    setSelectedRegionDistrictIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingItem) {
        await vehicleService.updateVehicle(editingItem.id, mapUiVehicleToApi(formData, (formData as any).externalTransportAccountUserId || companyIdFilter));
      } else {
        await vehicleService.createVehicle(mapUiVehicleToApi(formData, (formData as any).externalTransportAccountUserId || companyIdFilter));
      }
      
      await loadVehicles();
      closeForm();
    } catch (err) {
      console.error('Error saving vehicle:', err);
      showAlert({ tone: 'error', title: 'Kayıt Başarısız', message: 'Araç kaydedilirken bir hata oluştu.' });
    }
  };

  const handleDelete = async (id: string) => {
    if (await showConfirm({ title: 'Araç Sil', message: 'Bu aracı silmek istediğinizden emin misiniz?', confirmText: 'Sil', tone: 'warning' })) {
      try {
        await vehicleService.deleteVehicle(id);
        await loadVehicles();
      } catch (err) {
        console.error('Error deleting vehicle:', err);
        showAlert({ tone: 'error', title: 'Silme Başarısız', message: 'Araç silinirken bir hata oluştu.' });
      }
    }
  };

  const renderCell = (item: Vehicle, key: string) => {
    if (key === 'assignedDriverName') {
      const driverBadges = (item.activeDrivers || []).filter(d => d.id !== item.assignedDriverId);
      return (
        <div className="flex flex-col gap-1">
          {item.assignedDriverName ? (
            <span className="inline-flex self-start rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-xs font-semibold">
              {item.assignedDriverName}
            </span>
          ) : driverBadges.length === 0 ? (
            <span className="text-gray-400">-</span>
          ) : null}
          {driverBadges.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {driverBadges.map((driver) => (
                <span key={driver.id} className="inline-flex self-start rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs">
                  {driver.name}
                </span>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (key === 'excludeFromBulkAssignment') {
      return item.excludeFromBulkAssignment ? <span className="inline-flex rounded-full bg-red-100 text-red-700 px-2 py-1 text-xs font-semibold">Evet</span> : <span className="inline-flex rounded-full bg-emerald-100 text-emerald-700 px-2 py-1 text-xs font-semibold">Hayır</span>;
    }
    if (key === 'preferredDeliveryPoints') {
      return item.preferredDeliveryPoints && item.preferredDeliveryPoints.length > 0
        ? item.preferredDeliveryPoints.map((point) => point.label).join(', ')
        : '-';
    }
    if (key === 'regionDeliveryRestrictions') {
      return item.regionDeliveryRestrictions && item.regionDeliveryRestrictions.length > 0
        ? item.regionDeliveryRestrictions.map((point) => formatRegionRestrictionLabel(point)).join(', ')
        : '-';
    }
    if (key === 'status') {
      const colors = {
        'Aktif': 'bg-green-100 text-green-800',
        'Bakımda': 'bg-yellow-100 text-yellow-800',
        'Arızalı': 'bg-red-100 text-red-800',
        'Pasif': 'bg-gray-100 text-gray-800',
      };
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.status]}`}>
          {item.status}
        </span>
      );
    }
    if (key === 'ownershipType') {
      const colors = {
        'Filo': 'bg-blue-100 text-blue-800',
        'Kiralık': 'bg-purple-100 text-purple-800',
        'Spot': 'bg-orange-100 text-orange-800',
      };
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[item.ownershipType]}`}>
          {item.ownershipType}
        </span>
      );
    }
    if (['lastMaintenanceDate', 'nextMaintenanceDate', 'insuranceExpiry', 'inspectionExpiry'].includes(key) && item[key as keyof Vehicle]) {
      const value = String(item[key as keyof Vehicle] || '');
      const [year, month, day] = value.split('-');
      return year && month && day ? `${day}.${month}.${year}` : value;
    }
    if ((key === 'latitude' || key === 'longitude') && item[key as keyof Vehicle] != null) {
      return Number(item[key as keyof Vehicle]).toFixed(6);
    }
    return item[key as keyof Vehicle] || '-';
  };

  const renderActions = (item: Vehicle) => (
    <div className="flex gap-2">
      <button
        onClick={() => openForm(item)}
        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
      >
        <Edit className="w-4 h-4" />
      </button>
      <button
        onClick={() => handleDelete(item.id)}
        className="p-1 text-red-600 hover:bg-red-50 rounded"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );

  const normalizePlate = (value: string) => String(value || '').replace(/\s+/g, '').toLocaleUpperCase('tr-TR');
  const normalizeName = (value: string) => String(value || '').trim().toLocaleUpperCase('tr-TR');

  const parseExcelStatus = (value: string): Vehicle['status'] => {
    const text = String(value || '').trim().toLocaleLowerCase('tr-TR');
    if (text === 'aktif') return 'Aktif';
    if (text === 'bakımda' || text === 'bakimda') return 'Bakımda';
    if (text === 'arızalı' || text === 'arizali') return 'Arızalı';
    return 'Pasif';
  };

  const parseExcelFuelType = (value: string): Vehicle['fuelType'] => {
    const text = String(value || '').trim().toLocaleLowerCase('tr-TR');
    if (text === 'benzin') return 'Benzin';
    if (text === 'lpg') return 'LPG';
    if (text === 'elektrik') return 'Elektrik';
    return 'Dizel';
  };

  const handleBulkVehicleUpdateFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (!rows.length) {
        showAlert({ tone: 'warning', title: 'Boş Dosya', message: 'Excel dosyasında güncellenecek satır bulunamadı.' });
        return;
      }

      const vehicleByPlate = new Map(data.map((item) => [normalizePlate(item.plate), item]));
      let driverByName = new Map(internalDrivers.map((item) => [normalizeName(item.name), item]));

      let updated = 0;
      let skipped = 0;
      const issues: string[] = [];
      const unmatchedDrivers = new Set<string>();
      let workiomSyncDone = false;

      for (const row of rows) {
        const plate = normalizePlate(row['Çekici Plaka No']);
        if (!plate) {
          skipped += 1;
          continue;
        }
        const vehicle = vehicleByPlate.get(plate);
        if (!vehicle) {
          skipped += 1;
          issues.push(`${plate}: çekici bulunamadı`);
          continue;
        }

        const driverName = String(row['Sürücü'] || '').trim();
        let matchedDriver = driverName ? driverByName.get(normalizeName(driverName)) : null;
        if (driverName && !matchedDriver && !workiomSyncDone) {
          try {
            await driverService.syncInternalDriversFromWorkiom();
            const response: any = await driverService.getDrivers({ type: 'internal', status: 'active' });
            const items = Array.isArray(response?.data) ? response.data : [];
            const updatedDrivers = items.map((item: any) => ({ id: String(item.id), name: item.full_name || '-', plate: item.cekici_plaka || '' }));
            setInternalDrivers(updatedDrivers);
            driverByName = new Map(updatedDrivers.map((item) => [normalizeName(item.name), item]));
            workiomSyncDone = true;
            matchedDriver = driverName ? driverByName.get(normalizeName(driverName)) : null;
          } catch (syncError) {
            console.error('Workiom sync failed:', syncError);
            workiomSyncDone = true;
          }
        }
        if (driverName && !matchedDriver) {
          try {
            const createRes = await driverService.createDriver({ full_name: driverName, status: 'available' });
            if (createRes.success) {
              const newDriver = { id: String(createRes.data?.id || createRes.driver?.id || ''), name: driverName, plate: '' };
              driverByName.set(normalizeName(driverName), newDriver);
              matchedDriver = newDriver;
              const current = internalDrivers;
              setInternalDrivers([...current, newDriver]);
            }
          } catch (createError) {
            console.error('Driver creation failed:', createError);
          }
        }
        if (driverName && !matchedDriver) {
          skipped += 1;
          unmatchedDrivers.add(driverName);
          issues.push(`${plate}: sürücü oluşturulamadı (${driverName})`);
          continue;
        }

        const payload = mapUiVehicleToApi({
          ...vehicle,
          trailerPlate: String(row['Dorse Plaka No'] || '').trim() || '',
          assignedDriverId: matchedDriver?.id || '',
          brand: String(row['Marka'] || '').trim() || vehicle.brand,
          model: String(row['Model'] || '').trim() || vehicle.model,
          fuelType: parseExcelFuelType(String(row['Yakıt'] || '')),
          status: parseExcelStatus(String(row['Durum'] || '')),
        });

        await vehicleService.updateVehicle(vehicle.id, payload);
        updated += 1;
      }

      await loadVehicles();
      let detail = '';
      if (unmatchedDrivers.size > 0) {
        detail = `\nEşleşmeyen sürücüler:\n${[...unmatchedDrivers].map((n) => `  • ${n}`).join('\n')}`;
      } else if (issues.length > 0) {
        detail = `\nİlk sorunlar: ${issues.slice(0, 5).join(' | ')}`;
      }
      showAlert({ tone: updated > 0 ? 'success' : 'warning', title: 'Çekici Güncelleme Tamamlandı', message: `${updated} kayıt güncellendi, ${skipped} kayıt atlandı.${detail}` });
    } catch (error) {
      console.error('Bulk vehicle update error:', error);
      showAlert({ tone: 'error', title: 'Excel Güncelleme Başarısız', message: 'Çekici güncelleme dosyası işlenemedi.' });
    }
  };

  const handleBulkVehicleUpdateInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleBulkVehicleUpdateFile(file);
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{ownerTypeFilter === 'external' ? 'Kurumsal Dış Nakliye Çekicileri' : 'Çekici Yönetimi'}</h1>
            <p className="text-gray-500 mt-1">{ownerTypeFilter === 'external' ? 'Kurumsal dış nakliye şirketlerine bağlı çekicileri yönetin' : 'Filo araçlarını ve kiralık araçları yönetin'}</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-gray-500">Araçlar yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  const resolvedVehicleFields = (() => {
    const baseFields = metadataFields.length > 0 ? metadataFields.map((field) => field.key) : ['plate', 'trailerPlate', 'vehicleType', 'brand', 'model', 'year', 'capacity', 'volume', 'fuelType', 'ownershipType', 'externalTransportAccountUserId', 'regionDeliveryRestrictions', 'maxDailyIntraCityDeliveries', 'preferredDeliveryPoints', 'assignedDriverName', 'status', 'lastMaintenanceDate', 'nextMaintenanceDate', 'insuranceExpiry', 'inspectionExpiry', 'notes'];
    const filteredFields = baseFields.filter((fieldKey) => !isExternalOwnerView || !EXTERNAL_VEHICLE_HIDDEN_KEYS.has(fieldKey));
    if (!isExternalOwnerView) return filteredFields;
    const requiredExternalFields = ['trailerPlate', 'externalTransportAccountUserId'];
    let nextFields = filteredFields;
    for (const field of requiredExternalFields) {
      if (nextFields.includes(field)) continue;
      const ownershipIndex = nextFields.indexOf('ownershipType');
      const insertIndex = field === 'externalTransportAccountUserId' && ownershipIndex >= 0 ? ownershipIndex + 1 : Math.min(1, nextFields.length);
      nextFields = [...nextFields.slice(0, insertIndex), field, ...nextFields.slice(insertIndex)];
    }
    return nextFields;
  })();

  const renderVehicleField = (fieldKey: string) => {
    switch (fieldKey) {
      case 'plate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Plaka *</label><input type="text" required value={formData.plate || ''} onChange={(e) => setFormData({ ...formData, plate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="34 ABC 123" /></div>;
      case 'trailerPlate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Dorse Plaka</label>{isExternalOwnerView ? <input value={formData.trailerPlate || ''} onChange={(e) => setFormData({ ...formData, trailerPlate: normalizePlate(e.target.value) })} placeholder="Dorse plaka no" className="w-full border border-gray-300 rounded-lg px-3 py-2" /> : <select value={formData.trailerPlate || ''} onChange={(e) => setFormData({ ...formData, trailerPlate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="">Seçiniz</option>{trailers.map((trailer) => <option key={trailer.id} value={trailer.plate}>{trailer.plate}{trailer.linkedVehicleId && trailer.linkedVehicleId !== editingItem?.id ? ' (bağlı)' : ''}</option>)}</select>}</div>;
      case 'vehicleType': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Araç Tipi *</label><select required value={formData.vehicleType || 'Kamyon'} onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value as Vehicle['vehicleType'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="Tır">Tır</option><option value="Kamyon">Kamyon</option><option value="Kamyonet">Kamyonet</option><option value="Çekici">Çekici</option></select></div>;
      case 'brand': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Marka *</label><input type="text" required value={formData.brand || ''} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'model': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Model *</label><input type="text" required value={formData.model || ''} onChange={(e) => setFormData({ ...formData, model: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'year': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Yıl *</label><input type="number" required value={formData.year || ''} onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'capacity': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Kapasite (ton)</label><input type="number" step="0.01" value={formData.capacity || ''} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'volume': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Hacim</label><input type="number" step="0.01" value={formData.volume || ''} onChange={(e) => setFormData({ ...formData, volume: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'fuelType': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Yakıt Tipi</label><select value={formData.fuelType || 'Dizel'} onChange={(e) => setFormData({ ...formData, fuelType: e.target.value as Vehicle['fuelType'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="Dizel">Dizel</option><option value="Benzin">Benzin</option><option value="LPG">LPG</option><option value="Elektrik">Elektrik</option></select></div>;
      case 'ownershipType': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Mülkiyet</label><select value={formData.ownershipType || 'Filo'} onChange={(e) => setFormData({ ...formData, ownershipType: e.target.value as Vehicle['ownershipType'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="Filo">Filo</option><option value="Kiralık">Kiralık</option><option value="Spot">Spot</option></select></div>;
      case 'externalTransportAccountUserId': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Kurumsal Nakliye Firması</label><input list="vehicle-corporate-carriers" value={carrierCompanySearch} onChange={(e) => { const value = e.target.value; const selected = carrierCompanies.find((item) => item.name === value); setCarrierCompanySearch(value); setFormData({ ...formData, externalTransportAccountUserId: selected?.id || '', ownershipType: selected ? 'Spot' : (formData.ownershipType || 'Filo') }); }} placeholder="Firma adı yazın ve listeden seçin" className="w-full border border-gray-300 rounded-lg px-3 py-2" disabled={!!companyIdFilter} /><datalist id="vehicle-corporate-carriers">{carrierCompanies.map((item) => <option key={item.id} value={item.name} />)}</datalist><div className="mt-1 text-xs text-gray-500">Sadece ana kurumsal nakliye firmaları listelenir; alt kullanıcılar gösterilmez.</div></div>;
      case 'regionDeliveryRestrictions': return <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Bölge Teslimat Kısıtı</label><div className="grid grid-cols-1 gap-3"><select value={selectedRegionCityId} onChange={(e) => setSelectedRegionCityId(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="">İl seçin</option>{cities.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)}</select><div className="rounded-lg border border-gray-300 p-3 max-h-56 overflow-auto"><div className="mb-2 flex items-center justify-between"><span className="text-xs font-medium text-gray-600">İlçeler</span><button type="button" onClick={() => setSelectedRegionDistrictIds(regionDistricts.map((district) => String(district.id)))} disabled={!selectedRegionCityId || regionDistricts.length === 0} className="text-xs text-blue-600 disabled:text-gray-400">Tümünü Seç</button></div>{!selectedRegionCityId ? <div className="text-sm text-gray-500">Önce il seçin</div> : regionDistricts.length === 0 ? <div className="text-sm text-gray-500">İlçe bulunamadı</div> : <div className="grid grid-cols-1 gap-2">{regionDistricts.map((district) => <label key={district.id} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={selectedRegionDistrictIds.includes(String(district.id))} onChange={(e) => setSelectedRegionDistrictIds((current) => e.target.checked ? [...new Set([...current, String(district.id)])] : current.filter((item) => item !== String(district.id)))} /><span className="leading-5">{district.name}</span></label>)}</div>}</div><button type="button" onClick={addRegionRestriction} disabled={!selectedRegionCityId || selectedRegionDistrictIds.length === 0} className="justify-self-start rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Ekle</button></div><div className="mt-3 flex flex-wrap gap-2">{(formData.regionDeliveryRestrictions || []).length > 0 ? (formData.regionDeliveryRestrictions || []).map((item) => <span key={`${item.city}-${item.district}`} className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">{formatRegionRestrictionLabel(item)}<button type="button" onClick={() => removeRegionRestriction(item)} className="text-blue-700 hover:text-blue-900"><X className="h-3.5 w-3.5" /></button></span>) : <span className="text-sm text-gray-500">Kısıt yoksa araç tüm bölgelere atanabilir.</span>}</div><p className="mt-2 text-xs text-gray-500">Araç sadece burada seçili il / ilçelerin tamamı içinde kalan sevkiyatlara atanır. Yükleme, ana teslim ve ara boşaltma noktalarının hepsi bu listede olmalıdır.</p></div>;
      case 'maxDailyIntraCityDeliveries': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Günlük Şehir İçi Sipariş Limiti</label><input type="number" min={1} step={1} value={formData.maxDailyIntraCityDeliveries ?? ''} onChange={(e) => setFormData({ ...formData, maxDailyIntraCityDeliveries: e.target.value ? Number(e.target.value) : null })} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Örn. 3" /></div>;
      case 'excludeFromBulkAssignment': return <div><label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={formData.excludeFromBulkAssignment || false} onChange={(e) => setFormData({ ...formData, excludeFromBulkAssignment: e.target.checked })} className="h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-red-500" /><span><span className="text-sm font-medium text-gray-700">Toplu Sipariş Atamasında Kullanma</span><br /><span className="text-xs text-gray-500">Bu araç karar destek önizlemesinde ve toplu atamada alternatif olarak gösterilmez</span></span></label></div>;
      case 'preferredDeliveryPoints': return <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Statik Teslim Noktaları</label><input type="text" value={customerSearchTerm} onChange={(e) => setCustomerSearchTerm(e.target.value)} className="mb-2 w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Teslim noktası ara" /><div className="rounded-lg border border-gray-300 p-3 max-h-52 overflow-y-auto bg-white space-y-2">{filteredPreferredDeliveryCustomers.length > 0 ? filteredPreferredDeliveryCustomers.map((customer) => { const formItems = formData.preferredDeliveryPoints || []; const checked = formItems.some(x => String(x.id) === String(customer.id) || x.label === customer.name); return <label key={customer.id} className="flex items-start gap-3 text-sm"><input type="checkbox" checked={checked} onChange={(e) => setFormData((current) => { const currentItems = current.preferredDeliveryPoints || []; return { ...current, preferredDeliveryPoints: e.target.checked ? [...currentItems, { id: customer.id, label: customer.name }] : currentItems.filter((item) => String(item.id) !== String(customer.id) && item.label !== customer.name) }; })} className="mt-0.5 h-4 w-4 rounded border-gray-300" /><span><span className="font-medium text-gray-800">{customer.name}</span><span className="ml-2 text-xs text-gray-500">{[customer.code, customer.city, customer.district].filter(Boolean).join(' • ')}</span></span></label>; }) : <div className="text-sm text-gray-500">Kayıt bulunamadı</div>}</div><p className="mt-2 text-xs text-gray-500">Bu alan boş değilse araç sadece burada seçili müşterileri içeren siparişlere atanabilir. Seçili müşterileri içermeyen siparişlere atanmaz.</p></div>;
      case 'assignedDriverName': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Sürücü</label><select value={formData.assignedDriverName || ''} onChange={(e) => { const selected = internalDrivers.find((driver) => driver.name === e.target.value); setFormData({ ...formData, assignedDriverName: e.target.value, notes: formData.notes, assignedDriverId: selected?.id }); }} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="">Seçiniz</option>{internalDrivers.map((driver) => <option key={driver.id} value={driver.name}>{driver.name} {driver.plate ? `• ${driver.plate}` : ''}</option>)}</select></div>;
      case 'status': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Durum</label><select value={formData.status || 'Aktif'} onChange={(e) => setFormData({ ...formData, status: e.target.value as Vehicle['status'] })} className="w-full border border-gray-300 rounded-lg px-3 py-2"><option value="Aktif">Aktif</option><option value="Bakımda">Bakımda</option><option value="Arızalı">Arızalı</option><option value="Pasif">Pasif</option></select></div>;
      case 'lastMaintenanceDate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Son Bakım Tarihi</label><input type="date" value={formData.lastMaintenanceDate || ''} onChange={(e) => setFormData({ ...formData, lastMaintenanceDate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'nextMaintenanceDate': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Sonraki Bakım Tarihi</label><input type="date" value={formData.nextMaintenanceDate || ''} onChange={(e) => setFormData({ ...formData, nextMaintenanceDate: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'insuranceExpiry': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Sigorta Bitiş</label><input type="date" value={formData.insuranceExpiry || ''} onChange={(e) => setFormData({ ...formData, insuranceExpiry: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'inspectionExpiry': return <div><label className="block text-sm font-medium text-gray-700 mb-1">Muayene Bitiş</label><input type="date" value={formData.inspectionExpiry || ''} onChange={(e) => setFormData({ ...formData, inspectionExpiry: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></div>;
      case 'notes': return <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label><textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={3} /></div>;
      default: return null;
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{ownerTypeFilter === 'external' ? 'Kurumsal Dış Nakliye Çekicileri' : 'Çekici Yönetimi'}</h1>
            <p className="text-gray-500 mt-1">Filo araçlarını ve kiralık araçları yönetin</p>
          </div>
          <button
            onClick={() => openForm()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Yeni Araç
          </button>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button onClick={loadVehicles} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{ownerTypeFilter === 'external' ? 'Kurumsal Dış Nakliye Çekicileri' : 'Çekici Yönetimi'}</h1>
          <p className="text-gray-500 mt-1">{ownerTypeFilter === 'external' ? 'Kurumsal dış nakliye şirketlerine bağlı çekicileri yönetin' : 'Filo araçlarını ve kiralık araçları yönetin'}</p>
        </div>
        </div>
        <div className="workiom-toolbar-right">
        {!isExternalOwnerView && (
          <>
            <input ref={bulkUpdateInputRef} type="file" accept=".xlsx,.xls" onChange={handleBulkVehicleUpdateInput} className="hidden" />
            <button type="button" onClick={() => navigate('/vehicle-assignments')} className="workiom-outline-button">
              <ArrowRightLeft className="w-4 h-4" /> Geçici Görevlendirme
            </button>
            <button type="button" onClick={() => bulkUpdateInputRef.current?.click()} className="workiom-outline-button">
              <Upload className="w-4 h-4" /> Excel'den Çekici Güncelle
            </button>
          </>
        )}
        <button type="button" onClick={handleExportExcel} className="workiom-outline-button">
          Excele Aktar
        </button>
        <button
          onClick={() => openForm()}
          className="workiom-primary-button"
        >
          <Plus className="w-5 h-5" />
          Yeni Araç
        </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2 sm:px-6 sm:py-6 xl:grid-cols-5">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Toplam Araç</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{data.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Aktif</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {data.filter(v => v.status === 'Aktif').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Bakımda</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">
            {data.filter(v => v.status === 'Bakımda').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Filo</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {data.filter(v => v.ownershipType === 'Filo').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Kiralık</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">
            {data.filter(v => v.ownershipType === 'Kiralık').length}
          </div>
        </div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex flex-wrap items-center gap-3 w-full">
          <div className="workiom-search max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="Çekici, dorse, marka, model, sürücü ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="outline-none" />
          </div>
          <button onClick={() => setShowColumnFilters((prev) => !prev)} className={`workiom-outline-button ${showColumnFilters ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Filter className="w-4 h-4" /> Filtrele</button>
          <button onClick={() => setShowColumnsPanel((prev) => !prev)} className={`workiom-outline-button ${showColumnsPanel ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Eye className="w-4 h-4" /> Sütunlar</button>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowGrouping((prev) => !prev)} className={`workiom-outline-button ${showGrouping ? '!bg-blue-50 !border-blue-500 !text-blue-700' : ''}`}><Layers className="w-4 h-4" /> Gruplandır</button>
            {showGrouping && (
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="">Gruplama Yok</option>
                <option value="status">Durum</option>
                <option value="ownershipType">Mülkiyet</option>
                <option value="vehicleType">Araç Tipi</option>
                <option value="brand">Marka</option>
                <option value="year">Yıl</option>
                <option value="fuelType">Yakıt Tipi</option>
                <option value="assignedDriverName">Sürücü</option>
              </select>
            )}
          </div>
          <button type="button" onClick={() => void saveTableView()} className="workiom-outline-button"><Save className="w-4 h-4" /> Görünümü Kaydet</button>
          <button type="button" onClick={resetTableView} className="workiom-outline-button"><RotateCcw className="w-4 h-4" /> Sıfırla</button>
        </div>
      </div>

      {showColumnsPanel && (
        <div className="mx-4 mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:mx-6">
          <div className="mb-3 text-sm font-semibold text-slate-700">Sütun göster/gizle ve sıralama</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {allResolvedColumns.map((column) => {
              const visible = visibleColumnKeys.includes(column.key);
              const index = visibleColumnKeys.indexOf(column.key);
              return (
                <div key={column.key} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2">
                  <label className="flex min-w-0 items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={visible} onChange={(event) => setVisibleColumnKeys((current) => event.target.checked ? [...current, column.key] : current.filter((key) => key !== column.key))} />
                    <span className="truncate">{column.label}</span>
                  </label>
                  <div className="flex gap-1">
                    <button type="button" disabled={!visible || index <= 0} onClick={() => setVisibleColumnKeys((current) => { const next = [...current]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; return next; })} className="rounded border border-slate-200 px-2 py-1 text-xs disabled:opacity-30">Sol</button>
                    <button type="button" disabled={!visible || index < 0 || index >= visibleColumnKeys.length - 1} onClick={() => setVisibleColumnKeys((current) => { const next = [...current]; [next[index], next[index + 1]] = [next[index + 1], next[index]]; return next; })} className="rounded border border-slate-200 px-2 py-1 text-xs disabled:opacity-30">Sağ</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid gap-3 md:hidden">
        {sortedVehicles.map((item) => (
          <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-bold text-slate-900">{item.plate || '-'}</div>
                <div className="mt-1 text-xs text-slate-500">{item.vehicleType || item.brand || '-'}</div>
              </div>
              <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">{item.status || 'Aktif'}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
              <div><span className="font-semibold text-slate-500">Dorse:</span> {item.trailerPlate || '-'}</div>
              <div><span className="font-semibold text-slate-500">Firma:</span> {item.externalTransportAccountName || '-'}</div>
              <div><span className="font-semibold text-slate-500">Sürücü:</span> {item.assignedDriverName || '-'}</div>
              <div><span className="font-semibold text-slate-500">Sahiplik:</span> {item.ownershipType || '-'}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">{renderActions(item)}</div>
          </div>
        ))}
      </div>

      <div className="workiom-grid-wrap hidden border border-gray-200 bg-white visible-scrollbars md:block" style={{ height: 550, overflow: 'auto' }}>
          <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="workiom-grid-index text-left">#</th>
                {resolvedColumns.map((column) => (<th key={column.key} className="text-left">{renderSortableHeader(column.label, column.key)}</th>))}
                <th className="workiom-grid-sticky-right text-left">İşlemler</th>
              </tr>
              {showColumnFilters && (
                <tr>
                  <th className="workiom-grid-index"></th>
                  {resolvedColumns.map((column) => (
                    <th key={`filter-${column.key}`}><input value={columnFilters[column.key] || ''} onChange={(e) => setColumnFilters((prev) => ({ ...prev, [column.key]: e.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded border border-gray-300 px-2 py-1 text-xs" /></th>
                  ))}
                  <th className="workiom-grid-sticky-right"></th>
                </tr>
              )}
            </thead>
            <tbody>
              {Object.entries(groupedVehicles).flatMap(([groupKey, items]) => {
                const rows: any[] = [];
                if (groupBy) {
                  rows.push(<tr key={`group-${groupKey}`} className="bg-gray-100"><td colSpan={resolvedColumns.length + 2} className="px-3 py-2"><button type="button" onClick={() => setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))} className="flex items-center gap-2 text-sm font-medium text-gray-900">{collapsedGroups[groupKey] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}<span>{groupKey}</span><span className="text-gray-500">({items.length})</span></button></td></tr>);
                }
                if (!collapsedGroups[groupKey]) {
                  rows.push(...items.map((item, index) => (
                <tr key={item.id} onDoubleClick={() => openForm(item)} className="cursor-pointer">
                  <td className="workiom-grid-index">{index + 1}</td>
                  {resolvedColumns.map((column) => (
                    <td key={column.key}>
                      {renderCell(item, column.key)}
                    </td>
                  ))}
                  <td className="workiom-grid-sticky-right">
                    {renderActions(item)}
                  </td>
                </tr>
                  )));
                }
                return rows;
              })}
            </tbody>
          </table>
        {data.length === 0 && (
          <div className="text-center py-12 bg-white">
            <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Henüz araç eklenmemiş</p>
          </div>
        )}
      </div>


      {isFormOpen && (loadingEdit ? (
        <div className="flex items-center gap-3 px-6 py-4 bg-white rounded-lg shadow-xl">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Araç bilgileri yükleniyor...</span>
        </div>
      ) : (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-h-[90vh] sm:max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white p-4 sm:p-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Truck className="w-6 h-6" />
                {editingItem ? 'Araç Düzenle' : 'Yeni Araç Ekle'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto p-4 space-y-6 sm:p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {resolvedVehicleFields.map((fieldKey) => (
                  <div key={fieldKey} className={fieldKey === 'notes' ? 'col-span-2' : ''}>
                    {renderVehicleField(fieldKey)}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  <Save className="w-4 h-4" />
                  {editingItem ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ))}
    </div>
  );
}
